export interface AppConfiguration {
  initialized: boolean;
  instanceId: string;
  projectId: string;
  view: string;
  category?: string;
  domain: string;
  domainPath: string;
  code?: string;
  quest?: string;
}

export const defaultAppCfg: AppConfiguration = {
  initialized: false,
  instanceId: "instance",
  projectId: "",
  view: "",
  category: "",
  domain: "",
  domainPath: ""
}

export interface ViewInformation {
  name: string;
  path: string;
}

export const defaultViewState: ViewState = {
  initialized: false,
  loading: false,
  currentView: ""
}

export interface ViewState {
  initialized: boolean;
  loading: boolean;
  currentView: string;
}

export const defaultViewData: ViewData = {
  cfg: undefined,
  data: undefined,
  project: undefined
}

export interface ViewData {
  cfg: any;
  data: any;
  project: any;
}

export interface DomainConfiguration {
  initialized: boolean;
  description: string;
  lang: string;
  baseUrl: string;
  viewSettings: ViewSettings;
  projectSettings: ProjectSettings;
  serverSettings: ServerSettings;
  chatSettings?: ChatSettings;
  product: ProductConfiguration;
}

const defaultFeatures: FeatureConfig = {
  localStorageAvailable: false,
  compressData: false,
  cloudStorageAvailable: false,
  showProjectCode: false,
  showExitButton: false,
  showAssetsButton: false,
  assetsWithThumbs: false,
  showShareButton: false,
  exportAvailable: false,
  exportUserRoles: [],
  hasProjects: false,
  showResultCode: false,
  vfsMaxFileSizeMb: 2
}

const defaultProjectRestrictionSettings: ProjectRestrictions = {
  suffix: "",
  solutionRestricted: false,
  permittedUserRoles: []

}

const defaultViewSettings: ViewSettings = {
  appName: "",
  codeId: "",
  projectsView: "",
  launchView: "",
  editorView: "",
  portalView: "",
  features: defaultFeatures,
  projectRestrictionSettings: defaultProjectRestrictionSettings
}

const defaultProjectSettings: ProjectSettings = {
  projectPath: "",
  projectUrl: "",
  projectListFiles: [],
  i18nPath: "",
  assetsI18nPath: "",
  iconPath: "",
  urlParamEnabled: false,
  viewParamEnabled: false,
  errorFile: "",
  codeFunctionLine: 0,
  scriptCodeFunctionLine: 0
}

const defaultServerSettings: ServerSettings = {
  baseUrl: "",
  path: ""
}

const defaultProductSettings: ProductConfiguration = {
  name: "",
  views: [],
  successImg: "",
  shareMailBody: "",
  assetsLibPath: "",
  logoUrl: "",
  accountName: "",
  passwordResetLink: "",
  registrationUrl: "",
  userProfileLink: ""
}

export const defaultDomainCfg: DomainConfiguration = {
  initialized: false,
  description: "",
  lang: "",
  baseUrl: "",
  viewSettings: defaultViewSettings,
  projectSettings: defaultProjectSettings,
  serverSettings: defaultServerSettings,
  product: defaultProductSettings
}

interface ViewSettings {
  appName: string;
  debug?: boolean;
  codeId: string;
  projectsView: string;
  launchView: string;
  editorView: string;
  portalView: string;
  questView?: string;
  features: FeatureConfig;
  projectRestrictionSettings?: ProjectRestrictions;
  integrationStyles?: IntegrationStyles[];
}

interface FeatureConfig {
  localStorageAvailable?: boolean; //switch for localstorage saving of userdata
  compressData?: boolean; //switch for userdata compression
  cloudStorageAvailable?: boolean; //switch for cloudStorage
  showFirstTimeModalOnStart?: boolean; //switch for cloudStorage, default true
  registrationAvailable?: boolean; // switch for user registration feature, default false
  showProjectCode?: boolean; // switch show project code in ProjectNameLabel
  showExitButton?: boolean; // show home button
  showAssetsButton?: boolean;  // show assets library
  assetsWithThumbs?: boolean;  // load assets library with thumbs
  showShareButton?: boolean; // show share button
  shareCondition?: string; // show share button
  exportAvailable?: boolean; // enable project export
  exportUserRoles?: string[]; // enable project export for some logged in users only
  hasProjects?: boolean; // switch if projects view is enabled
  showResultCode?: boolean; //show result code area in editor
  collaborationEnabled?: boolean; //is collaboration enabled?  
  hasOnboarding?: boolean; //has onboarding enabled?
  permittedDataViewUser?: string[]; //data view enabled? 
  newProjectCode?: string; //new project code? 
  learndashTrigger?: string; //selector for learndash trigger on success and continue? 
  categoryFilterEnabled?: boolean; //is category filter enabled?  
  chatAvailable?: boolean; //is chat bot available?
  browserAgentEnabled?: boolean; //KiRa AI Agent (V2 multifile) instead of support chat in editor
  initialChatRequests?: number; //number of initial available chat requests
  gamificationEnabled?: boolean; //is gamification enabled?
  darkModeEnabled?: boolean; //is dark mode enabled?
  darkModeEditorOnly?: boolean; //is dark mode only enabled for editor view?
  showThemeButton?: boolean; //show theme button?
  showNextButton?: boolean; //show next project button?
  vfsMaxFileSizeMb?: number; //max size for VFS media uploads in MB
}

interface IntegrationStyles {
  viewName: string;
  styles?: IntegrationStyle[];
}

interface IntegrationStyle {
  selector?: string;
  action?: string;
  className?: string;
}

export interface CloudStorageData {
  loggedIn: boolean;
  userName: string;
}

export interface LocalStorageData {
  version?: string;
  userId: string;
  code: string;
  sources: string;
  assets: string;
  projectProgress?: number; //last finished subtask
  projectFinished?: boolean;
  projectName?: string;
  projectHint?: string;
  projectTask?: string;
  chatMessages?: any[];
  agentChatMessages?: any[];
  agentChatId?: string;
  lastUsedModelId?: string;
}

export interface FileData {
  fileName: string;
  content: string;
}

interface ProjectSettings {
  projectPath: string;
  projectUrl: string; // url where the project.json is located
  projectListFiles: string[]; //path to a list of allowed project code list json files
  i18nPath: string; //path where i18n files are stored
  assetsI18nPath: string; //path where assets i18n files are stored
  iconPath: string; //path where app icons are stored
  urlParamEnabled: boolean; //are project parameter from url allowed?
  viewParamEnabled: boolean; //are view parameters from url allowed?
  codeFromCfgEnabled?: boolean; //is code from app configuration allowed?
  chatContextEnabled?: boolean; //is chat context enabled?
  useProjectContextByDefault?: boolean; //use project context by default?
  contextSelector?: string; //selector for context element
  errorFile: string; // path to the error file template
  codeFunctionLine: number; //line number on which the function keyword stays. used for error line calculation
  scriptCodeFunctionLine: number; //line number on which the function keyword stays based on script-tag! used for error line calculation
  questPath?: string;
  questUrl?: string;
}

interface ServerSettings {
  backendUrl?: string; //url for the backend language server (glasofen)
  connPort?: string; //port for socket connection to the backend language server
  ioPort?: string; //port for socket input/output to the backend language server
  replaceKey?: string; // key for replacing cfg setting for terminal iframe
  baseUrl: string;
  path: string;
  partyWebSocketServer?: string;
  supportedBackendLangs?: string[];
}

interface ChatSettings {
  chatUrl: string;
  workflow: string;
  botName: string;
  userName: string;
  startMessage: string;
  quotaMessage: string;
  creditPurchaseUrl: string;
  noAuth?: boolean
  disclaimer: string;
  initialLimit?: number;
  useWebsocket?: boolean;
  supportedCodeLangs?: string[];
  vaibSupport?: boolean;
}

interface ProductConfiguration {
  name: string;
  views: ViewInformation[];
  successImg: string;
  shareMailBody: string;
  assetsLibPath: string
  logoUrl: string;
  homeUrl?: string;
  accountName: string;
  passwordResetLink: string;
  registrationUrl?: string;
  userProfileLink: string;
  onboarding?: string;
  errorHelp?: string;
  private?: boolean;
  privateUrl?: string;
  activationUrl?: string;
}

export interface AllowedProjectsList {
  projects: AllowedProject[];
}

export interface AllowedProject {
  code: string;
  directory?: string;
  icon?: string | null;
  catkey?: string;
}

export interface ProjectRestrictions {
  suffix?: string;
  solutionRestricted: boolean;
  permittedUserRoles: string[];
}

export interface QueryParams {
  projectParamEnabled: boolean;
  project: string;
  viewParamEnabled: boolean;
  view: string;
  collaborationEnabled: boolean;
  party: string;
  categoryFilterEnabled: boolean;
  categoryFilter: string;
  quest: string;
}

/*
  * Project Data
*/
export interface ProjectData {
  invalid: boolean; //is project invalid?
  type?: string; //type of project
  language: string; //language of project
  project: string; //project code
  content: string; //project content reference to content.html
  nextProject: string; //next project code, after successfull completion
  learning?: string; //learning content for successmodal  
  task: string; //task description text for the project
  subTasks?: ProjectTask[]; //subtasks. eval in subTasks only working if no eval is defined
  steps: []; //steps for tutorialmode
  hint: string; //hint for project
  eval?: TaskEvaluation; //task evaluation. only working if no subtasks are defined
  template: string; //base template for project
  /** Optional: Pfad zu .js relativ zur project.json; wird vor dem body-Tag ins Vorschau-HTML eingefügt (z. B. iframe-Anker-Fix für srcdoc). Leer/fehlend = aus. */
  injectJs?: string;
  init: string; //init reference to init.data with initialization code
  initKey: string; //key for init data replacement
  code: string; //code reference to code.data with code for project
  tabs: ProjectDataTab[]; //multitabs for project
  replaceKey: string; //key for code replacement
  assets: string; //assets reference to assets.json with assets for project
  assetsKey: string; //key for assets replacement
  solution?: string; //solution reference to solution.data with solution for project
  templateProject?: ProjectData; //template project field description for new projects 
  manifest?: ProjectExport | string; //manifest reference to manifest.json with export settings for project
  startSessionOnStartup?: boolean; // flag for start session on startup
  hideEditor?: boolean; // flag for hiding text-editor in editor view
}

/**
 * Repräsentiert eine spezifische Aufgabe innerhalb eines Projekts.
 */
export interface ProjectTask {
  nr: number;             // Die eindeutige Nummer der Aufgabe.
  text: string;           // Der Haupttext oder die Beschreibung der Aufgabe.
  hint: string;           // Ein optionaler Hinweis, der den Benutzern bei der Aufgabe helfen kann.
  eval: TaskEvaluation;   // Bewertungskriterien für die Aufgabe.
}

/**
 * Definiert die Bewertungskriterien für eine spezifische Aufgabe.
 */
export interface TaskEvaluation {
  type: string;           // Der Typ der Bewertung (z.B. "multiple-choice", "text-input" usw.).
  id?: string;            // Eine optionale ID, die zur Identifizierung des Bewertungselements verwendet werden kann.
  value?: string;         // Ein optionaler Wert, der für die Bewertung verwendet wird.
  start?: string;         // Ein optionaler Startwert (könnte für Bereichsbewertungen verwendet werden).
  end?: string;           // Ein optionaler Endwert (könnte für Bereichsbewertungen verwendet werden).
}

/**
 * Repräsentiert die Exporteinstellungen für ein Projekt.
 */
export interface ProjectExport {
  type: string;           // Der Typ des Exports (z.B. "zip", "tar" usw.).
  files?: ExportFile[];   // Eine Liste von Dateien, die exportiert werden sollen.
}

/**
 * Stellt eine spezifische Datei dar, die als Teil eines Projekts exportiert werden soll.
 */
export interface ExportFile {
  name: string;           // Der Name der Datei.
  type: string;           // Der Dateityp oder das Format (z.B. "json", "xml" usw.).
}

export interface TemplateField {
  key: string;
  destination: string;
  text: string;
  value?: string;
}

export interface ProjectDataTab {
  index: number;
  code: string;
  active: boolean;
  name: string;
  language: string;
  replaceKey: string;
}

export interface EditorData {
  projectSource: string;
  tabs: ProjectDataTab[];
  templateCode: string;
  initCode: string;
  initLOC: number;
  projectAssets: string;
  solutionCode: string;
  contextContent?: string;
  /** Inhalt von injectJs-Datei nach Projekt-Laden; nur für HTML-Zusammenbau */
  injectJsCode?: string;
}

export interface QuestEntry {
  id: string;
  description?: string;
  listed: boolean;
  permittedRoles?: string[];
  achievable: boolean;
  image?: string;
}

export interface QuestData {
  name: string;
  path: QuestPath[];
}

export interface QuestPath {
  key: number;
  title: string;
  shape: string;
  icon?: string;
  image?: string;
  projectCode?: string;
}

export interface QuestEntry {
  id: string;
  description?: string;
  listed: boolean;
  permittedRoles?: string[];
  achievable: boolean;
  image?: string;
}

export interface QuestData {
  name: string;
  path: QuestPath[];
}

export interface QuestPath {
  key: number;
  title: string;
  shape: string;
  icon?: string;
  image?: string;
  projectCode?: string;
}

export interface AssetsLibrary {
  tabs: AssetsLibTab[];
}

export interface AssetsLibTab {
  name: string;
  file: string;
  files?: any[];
  type: string;
  code: string;
  data: AssetsLibEntry[];
}

export interface AssetsLibEntry {
  name: string;
  file: string;
  type: string;
  image?: string;
  content?: string;
  assets?: AssetsLibEntry[];
}

export interface ErrorHelpData {
  errorHelp: ErrorHelp[];
}

export interface ErrorHelp {
  name: string;
  keyword: string;
  translated: string;
  shortHelp: string;
}

export interface OnboardingData {
  onboardingProject: string;
  journey: OnboardingJourney[];
}

export interface OnboardingJourney {
  mainStep: number;
  steps: OnboardingStep[];
  view: string;
}

export interface OnboardingStep {
  step: number;
  target: string;

  content?: string;
  //contentUrl?: string;
}

export interface CloudUser {
  loggedIn: boolean;
  userName: string;
  name?: string;
  nonce?: string;
  userCaps?: string;
  error?: string;
}

export interface CloudData {
  deleted: boolean;
  loaded: boolean;
  cloudStateLoaded: boolean;
  loadError?: string;
  code: string;
  sources: string;
  tabs: [];
  assets: string;
  replaceKey: string;
  initKey: string;
  assetsKey: string;
  initCode: string;
  projectAssets: string;
  templateCode: string;
  projectName?: string;
  projectTask?: string;
  projectHint?: string;
  chatMessages?: any[];
  agentChatMessages?: any[];
  agentChatId?: string;
  lastUsedModelId?: string;
}

export interface ProjectListEntry {
  id: string;
  name: string;
  shareId?: string;
  screenshot?: string;
  icon?: string;
  status: string;
  catkey?: string;
}

export interface ProjectListData {
  list: ProjectListEntry[];
  status?: string;
}

//export interface StatsData
